/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, memo } from 'react';
import { useTheme } from '@mui/material/styles';
import useMenu from 'hooks/useMenu';
import { useDispatch } from 'react-redux';
import { handleChangeMenu } from 'store/menu/menuSlice';
import { Button, ListItemButton, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material';
import { ItemChildrenProps } from 'interface/menu/menuInfo';
import { useNavigate, useLocation } from 'react-router-dom';
import { closeDialog, openDialog } from 'store/dialog/dialogSlice';
import InfoDialog from 'components/dialog/InfoDialog';

interface IMenuItemProps {
  item: ItemChildrenProps;
  level: number;
}

function MenuItem({ item, level }: IMenuItemProps): JSX.Element {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { open, changeMenu } = useMenu();
  const handleContinue = () => dispatch(closeDialog());
  const handleMove = async (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    event.preventDefault();
    await handleContinue();
    await handleCmnMove(id);
  };
  const handleCmnMove = (id: string) => {
    dispatch(handleChangeMenu({ changeMenu: [id] }));
    navigate(item.url);
  };
  const isWarningPopup = ['/admin/diagnostic-management/regist', '/admin/account'];
  const handleClick = (id: string) => {
    if (
      isWarningPopup.includes(location.pathname) ||
      ('/admin/project/enter-information'.includes(location.pathname) && location.state?.rowInfo)
    ) {
      dispatch(
        openDialog({
          content: (
            <InfoDialog
              contents="작업한 내용이 저장되지 않습니다."
              buttonInfo={
                <Stack direction="row" spacing={2} alignContent="center">
                  <Button variant="outlined" color="primary" onClick={handleContinue}>
                    계속 작성
                  </Button>
                  <Button variant="contained" color="primary" onClick={event => handleMove(event, id)}>
                    이동하기
                  </Button>
                </Stack>
              }
            />
          ),
        }),
      );
      return;
    }
    handleCmnMove(id);
  };

  const Icon = item.icon;
  const itemIcon = item.icon ? <Icon style={{ fontSize: '1.25rem' }} /> : false;
  const isSelected = changeMenu.findIndex(id => id === item.id) > -1;

  const textColor = 'mind.menu.notSelected';
  const iconSelectedColor = 'mind.blue.azul';
  const iconColor = 'mind.menu.icon';
  let conditionColor;
  if (level === 1 && !isSelected) {
    conditionColor = 'mind.menu.notSelected';
  } else if (level === 1 && isSelected) {
    conditionColor = iconSelectedColor;
  } else {
    conditionColor = isSelected ? iconSelectedColor : textColor;
  }

  const childrenList = item.children?.map(subItem => {
    switch (subItem.type) {
      default:
        return null;
    }
  });

  useEffect(() => {
    const currentIndex = location.pathname
      .toString()
      .split('/')
      .findIndex(id => id === item.id);
    if (currentIndex > -1) {
      dispatch(handleChangeMenu({ changeMenu: [item.id] }));
    }
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <ListItemButton
        disabled={item?.disabled || false}
        onClick={() => handleClick(item.id)}
        selected={isSelected}
        sx={{
          zIndex: 1201,
          margin: '0.0625rem 1rem',
          padding: '0.625rem 1rem',
          pl: open && level !== 1 ? '2.8rem' : '1rem',
          height: 40,
          borderRadius: '0.375rem',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: '-1rem',
            width: '0.25rem',
            height: '2.5rem',
            borderRadius: 0,
            background: isSelected && level === 1 ? `${theme.palette.mind.blue.azul}` : 'transparent',
          },
          ...(open && {
            '&:hover': {
              bgcolor: 'primary.lighter',
            },
            '&.Mui-selected': {
              color: iconSelectedColor,
              fontWeight: 500,
              backgroundColor: isSelected && level === 1 ? 'rgba(49, 84, 234, 0.08)' : 'transparent',
              '&:hover': {
                color: iconSelectedColor,
                bgcolor: 'primary.lighter',
              },
            },
          }),
          ...(!open && {
            '&:hover': {
              bgcolor: 'transparent',
            },
            '&.Mui-selected': {
              '&:hover': {
                bgcolor: 'transparent',
              },
              bgcolor: 'transparent',
            },
          }),
        }}
      >
        {itemIcon && (
          <ListItemIcon
            sx={{
              minWidth: 28,
              color: iconColor,
              ...(!open && {
                borderRadius: 1.5,
                width: 36,
                height: 36,
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  bgcolor: 'secondary.lighter',
                },
              }),
              ...(!open &&
                isSelected && {
                  bgcolor: 'primary.lighter',
                  '&:hover': {
                    bgcolor: 'primary.lighter',
                  },
                }),
            }}
          >
            {itemIcon}
          </ListItemIcon>
        )}
        {(open || (!open && level !== 1)) && (
          <ListItemText
            primary={
              <Typography
                sx={{
                  color: conditionColor,
                  fontSize: 14,
                  fontWeight: isSelected ? 700 : 400,
                }}
              >
                {item.title}
              </Typography>
            }
          />
        )}
      </ListItemButton>
      {childrenList}
    </>
  );
}

export default memo(MenuItem);
